import React from 'react';
import { Card } from './Card';

export interface LoadingStateProps {
  /** Descriptive text shown during loading */
  message?: string;
  /** Size variant for the spinner */
  size?: 'sm' | 'md' | 'lg';
  /** Whether to show as a full card or inline */
  variant?: 'card' | 'inline' | 'overlay';
  /** Skeleton layout style */
  skeletonVariant?: 'default' | 'stats' | 'chart' | 'table' | 'chat' | 'minimal';
  /** Custom className */
  className?: string;
}

/**
 * LoadingState - Consistent loading indicator component
 *
 * Usage:
 * ```tsx
 * <LoadingState message="Loading your trades..." />
 * <LoadingState size="sm" variant="inline" />
 * <LoadingState skeletonVariant="stats" message="Loading analytics..." />
 * ```
 */
export const LoadingState: React.FC<LoadingStateProps> = ({
  message = 'Loading...',
  size = 'md',
  variant = 'card',
  skeletonVariant = 'default',
  className = '',
}) => {
  const sizeClasses = {
    sm: 'w-5 h-5 border-2',
    md: 'w-8 h-8 border-3',
    lg: 'w-12 h-12 border-4',
  };

  const Spinner = () => (
    <div
      className={`${sizeClasses[size]} border-st-primary border-t-transparent rounded-full animate-spin`}
      role="status"
      aria-label={message}
    />
  );

  if (variant === 'inline') {
    return (
      <div
        className={`flex items-center gap-3 ${className}`}
        role="status"
        aria-live="polite"
        aria-label={message}
      >
        <Spinner />
        {message && <span className="text-sm text-slate-400">{message}</span>}
        <span className="sr-only">{message}</span>
      </div>
    );
  }

  if (variant === 'overlay') {
    return (
      <div
        className={`absolute inset-0 bg-st-dark/80 backdrop-blur-sm flex items-center justify-center z-50 ${className}`}
        role="status"
        aria-live="polite"
        aria-label={message}
      >
        <div className="flex flex-col items-center gap-4">
          <Spinner />
          {message && <span className="text-sm text-slate-400">{message}</span>}
        </div>
        <span className="sr-only">{message}</span>
      </div>
    );
  }

  // Render skeleton based on variant
  const renderSkeleton = () => {
    switch (skeletonVariant) {
      case 'stats':
        return (
          <div className="space-y-6">
            {/* Stat cards grid */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              {Array.from({ length: 4 }).map((_, i) => (
                <div key={i} className="bg-st-card border border-slate-800 rounded-lg p-4">
                  <div className="animate-pulse space-y-2">
                    <div className="h-3 w-16 bg-slate-700 rounded" />
                    <div className="h-7 w-20 bg-slate-700 rounded" />
                  </div>
                </div>
              ))}
            </div>
            {/* Content area */}
            <div className="animate-pulse space-y-3">
              <div className="h-4 w-full bg-slate-800 rounded" />
              <div className="h-4 w-5/6 bg-slate-800 rounded" />
              <div className="h-4 w-2/3 bg-slate-800 rounded" />
            </div>
          </div>
        );

      case 'chart':
        return (
          <div className="space-y-6">
            {/* Stat cards */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              {Array.from({ length: 4 }).map((_, i) => (
                <div key={i} className="bg-st-card border border-slate-800 rounded-lg p-4">
                  <div className="animate-pulse space-y-2">
                    <div className="h-3 w-16 bg-slate-700 rounded" />
                    <div className="h-7 w-20 bg-slate-700 rounded" />
                  </div>
                </div>
              ))}
            </div>
            {/* Chart skeleton */}
            <div className="bg-st-card border border-slate-800 rounded-lg p-4 h-[300px]">
              <div className="animate-pulse h-full flex flex-col">
                <div className="flex items-center justify-between mb-4">
                  <div className="h-5 w-32 bg-slate-700 rounded" />
                  <div className="flex gap-2">
                    <div className="h-6 w-16 bg-slate-700 rounded" />
                    <div className="h-6 w-16 bg-slate-700 rounded" />
                  </div>
                </div>
                <div className="flex-1 flex items-end gap-1 pb-4">
                  {Array.from({ length: 15 }).map((_, i) => (
                    <div
                      key={i}
                      className="flex-1 bg-slate-700 rounded-t"
                      style={{ height: `${Math.random() * 60 + 20}%` }}
                    />
                  ))}
                </div>
              </div>
            </div>
          </div>
        );

      case 'table':
        return (
          <div className="space-y-6">
            {/* Summary stats */}
            <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
              {Array.from({ length: 5 }).map((_, i) => (
                <div key={i} className="bg-st-card border border-slate-800 rounded-lg p-4">
                  <div className="animate-pulse space-y-2">
                    <div className="h-3 w-16 bg-slate-700 rounded" />
                    <div className="h-7 w-20 bg-slate-700 rounded" />
                  </div>
                </div>
              ))}
            </div>
            {/* Table skeleton */}
            <div className="bg-st-card border border-slate-800 rounded-lg overflow-hidden">
              <div className="animate-pulse">
                <div className="flex gap-4 p-4 border-b border-slate-700 bg-slate-800/30">
                  {Array.from({ length: 5 }).map((_, i) => (
                    <div key={i} className="h-4 bg-slate-600 rounded flex-1" />
                  ))}
                </div>
                {Array.from({ length: 5 }).map((_, rowIndex) => (
                  <div key={rowIndex} className="flex gap-4 p-4 border-b border-slate-800/50">
                    {Array.from({ length: 5 }).map((_, colIndex) => (
                      <div key={colIndex} className="h-4 bg-slate-700 rounded flex-1" />
                    ))}
                  </div>
                ))}
              </div>
            </div>
          </div>
        );

      case 'chat':
        return (
          <div className="space-y-4">
            {/* Messages skeleton */}
            {Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className={`flex items-start gap-4 ${i % 2 === 1 ? 'flex-row-reverse' : ''}`}>
                <div className="w-10 h-10 rounded-full bg-slate-700 shrink-0 animate-pulse" />
                <div className={`flex-1 max-w-[80%] space-y-2 p-4 rounded-2xl animate-pulse ${i % 2 === 1 ? 'bg-st-primary/10 ml-auto' : 'bg-slate-800/50'}`}>
                  <div className="h-4 w-full bg-slate-600 rounded" />
                  <div className="h-4 w-5/6 bg-slate-600 rounded" />
                  <div className="h-4 w-2/3 bg-slate-600 rounded" />
                </div>
              </div>
            ))}
          </div>
        );

      case 'minimal':
        return null;

      default:
        return (
          <div className="animate-pulse space-y-3 mt-6">
            <div className="h-4 w-40 bg-slate-800 rounded mx-auto" />
            <div className="h-3 w-full bg-slate-800 rounded" />
            <div className="h-3 w-5/6 bg-slate-800 rounded" />
            <div className="h-3 w-2/3 bg-slate-800 rounded" />
          </div>
        );
    }
  };

  // Card variant (default)
  return (
    <div role="status" aria-live="polite" aria-label={message}>
      {skeletonVariant !== 'minimal' && skeletonVariant !== 'default' ? (
        <div className={className}>
          <div className="flex flex-col items-center justify-center gap-4 mb-6">
            <Spinner />
            {message && <span className="text-slate-400">{message}</span>}
          </div>
          {renderSkeleton()}
        </div>
      ) : (
        <Card hoverEffect={false} className={`p-8 ${className}`}>
          <div className="flex flex-col items-center justify-center gap-4">
            <Spinner />
            {message && <span className="text-slate-400">{message}</span>}
          </div>
          {renderSkeleton()}
        </Card>
      )}
      <span className="sr-only">{message}</span>
    </div>
  );
};

export default LoadingState;
